import React, { useState } from 'react';
import { X, CheckCircle2, AlertCircle, Lock } from 'lucide-react';
import { FormSelect } from './FormSelect';
interface OrderRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  sector?: string;
}
export function OrderRequestModal({
  isOpen,
  onClose,
  productName,
  sector = ''
}: OrderRequestModalProps) {
  const [formData, setFormData] = useState({
    orgName: '',
    firstName: '',
    lastName: '',
    email: '',
    sector: sector || '',
    quantity: '',
    deliveryLocation: '',
    poReference: '',
    requiredDate: '',
    notes: '',
    budgetHolderEmail: ''
  });
  const [submitted, setSubmitted] = useState(false);
  if (!isOpen) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" data-id="element-231">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-232">
        <div className="fixed inset-0 bg-navy-900 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-233"></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-234">
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full rounded-sm border border-steel-200" data-id="element-235">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-236">
            <div className="flex justify-between items-start mb-6" data-id="element-237">
              <h3 className="text-2xl font-bold text-navy-900" id="modal-title" data-id="element-238">
                Request an Order
              </h3>
              <button onClick={onClose} className="bg-white rounded-md text-steel-400 hover:text-steel-500 focus:outline-none" data-id="element-239">
                <span className="sr-only" data-id="element-240">Close</span>
                <X className="h-6 w-6" aria-hidden="true" data-id="element-241" />
              </button>
            </div>

            {!submitted ? <form onSubmit={handleSubmit} className="space-y-6" data-id="element-242">
                <div className="bg-slate-50 p-4 border border-steel-200 rounded-sm mb-6" data-id="element-243">
                  <p className="text-sm text-steel-600" data-id="element-244">
                    <span className="font-bold text-navy-900" data-id="element-245">Product:</span>{' '}
                    {productName}
                  </p>
                  <p className="text-xs text-steel-500 mt-1" data-id="element-246">
                    This request will be submitted for approval. No payment is
                    taken at this stage.
                  </p>
                  <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-steel-200" data-id="element-247">
                    <Lock className="w-3 h-3 text-cg-orange-600 flex-shrink-0" data-id="element-248" />
                    <p className="text-[11px] text-steel-500" data-id="element-249">
                      Encrypted submission. All data treated in strict
                      confidence.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-250">
                  <div data-id="element-251">
                    <label htmlFor="firstName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-252">
                      First Name *
                    </label>
                    <input type="text" id="firstName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-2 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.firstName} onChange={handleChange} data-id="element-253" />
                  </div>
                  <div data-id="element-254">
                    <label htmlFor="lastName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-255">
                      Last Name *
                    </label>
                    <input type="text" id="lastName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-2 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.lastName} onChange={handleChange} data-id="element-256" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-257">
                  <div data-id="element-258">
                    <label htmlFor="email" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-259">
                      Work Email *
                    </label>
                    <input type="email" id="email" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-2 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.email} onChange={handleChange} data-id="element-260" />
                  </div>
                  <div data-id="element-261">
                    <label htmlFor="orgName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-262">
                      Organisation Name *
                    </label>
                    <input type="text" id="orgName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-2 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.orgName} onChange={handleChange} data-id="element-263" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-264">
                  <div data-id="element-265">
                    <FormSelect id="sector" label="Sector" required value={formData.sector} onChange={handleChange} className="py-2" // Match the input height in this modal
                data-id="element-266">
                      <option value="" data-id="element-267">Select Sector</option>
                      <option value="Defence" data-id="element-268">Defence</option>
                      <option value="Police" data-id="element-269">Police</option>
                      <option value="Prisons" data-id="element-270">Prisons</option>
                      <option value="Medical" data-id="element-271">Medical</option>
                      <option value="Other" data-id="element-272">Other</option>
                    </FormSelect>
                  </div>
                  <div data-id="element-273">
                    <label htmlFor="quantity" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-274">
                      Quantity *
                    </label>
                    <input type="text" id="quantity" required placeholder="e.g. 50 units" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-2 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.quantity} onChange={handleChange} data-id="element-275" />
                  </div>
                </div>

                <div data-id="element-276">
                  <label htmlFor="deliveryLocation" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-277">
                    Delivery Location (Country + Address Line) *
                  </label>
                  <input type="text" id="deliveryLocation" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-2 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.deliveryLocation} onChange={handleChange} data-id="element-278" />
                </div>

                <div className="border-t border-steel-200 pt-6 mt-6" data-id="element-279">
                  <h4 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4" data-id="element-280">
                    Optional Details
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-281">
                    <div data-id="element-282">
                      <label htmlFor="poReference" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-283">
                        Purchase Order Ref
                      </label>
                      <input type="text" id="poReference" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-2 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.poReference} onChange={handleChange} data-id="element-284" />
                    </div>
                    <div data-id="element-285">
                      <label htmlFor="requiredDate" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-286">
                        Required Date/Timeframe
                      </label>
                      <input type="text" id="requiredDate" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-2 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.requiredDate} onChange={handleChange} data-id="element-287" />
                    </div>
                  </div>

                  <div className="mb-6" data-id="element-288">
                    <label htmlFor="budgetHolderEmail" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-289">
                      Budget Holder / Procurement Contact Email
                    </label>
                    <input type="email" id="budgetHolderEmail" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-2 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.budgetHolderEmail} onChange={handleChange} data-id="element-290" />
                  </div>

                  <div data-id="element-291">
                    <label htmlFor="notes" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-292">
                      Notes (Specification/Configuration)
                    </label>
                    <textarea id="notes" rows={3} className="w-full bg-white border border-steel-300 rounded-sm px-4 py-2 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.notes} onChange={handleChange} data-id="element-293" />
                  </div>
                </div>

                <div className="flex justify-end gap-4 pt-4" data-id="element-294">
                  <button type="button" onClick={onClose} className="px-6 py-3 border border-steel-300 text-navy-900 font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-steel-50 transition-colors" data-id="element-295">
                    Cancel
                  </button>
                  <button type="submit" className="px-6 py-3 bg-cg-orange-600 text-white font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-cg-orange-500 transition-colors" data-id="element-296">
                    Submit Order Request
                  </button>
                </div>

                <div className="flex items-start gap-2 pt-2" data-id="element-297">
                  <Lock className="w-3 h-3 text-cg-orange-600 mt-0.5 flex-shrink-0" data-id="element-298" />
                  <p className="text-[11px] text-steel-500 leading-relaxed" data-id="element-299">
                    This form is encrypted and all submissions are treated in
                    strict confidence. Your information will not be shared with
                    third parties.
                  </p>
                </div>
              </form> : <div className="text-center py-12" data-id="element-300">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6" data-id="element-301">
                  <CheckCircle2 className="h-10 w-10 text-green-600" data-id="element-302" />
                </div>
                <h3 className="text-2xl font-bold text-navy-900 mb-2" data-id="element-303">
                  Order Request Submitted
                </h3>
                <p className="text-lg text-steel-600 mb-6" data-id="element-304">
                  Status:{' '}
                  <span className="font-bold text-cg-orange-600" data-id="element-305">
                    Pending Approval
                  </span>
                </p>
                <div className="bg-slate-50 p-6 rounded-sm border border-steel-200 max-w-lg mx-auto text-left mb-8" data-id="element-306">
                  <h4 className="font-bold text-navy-900 mb-2 flex items-center gap-2" data-id="element-307">
                    <AlertCircle className="w-4 h-4 text-cg-orange-600" data-id="element-308" /> Next
                    Steps
                  </h4>
                  <ul className="list-disc list-inside text-sm text-steel-600 space-y-2" data-id="element-309">
                    <li data-id="element-310">
                      Your request has been sent to our procurement team for
                      review.
                    </li>
                    <li data-id="element-311">
                      We will confirm availability, specification, and pricing.
                    </li>
                    <li data-id="element-312">
                      An acknowledgement email has been sent to {formData.email}
                      .
                    </li>
                    <li data-id="element-313">Payment will be arranged after approval.</li>
                  </ul>
                </div>
                <button type="button" onClick={onClose} className="px-8 py-3 bg-navy-900 text-white font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-navy-800 transition-colors" data-id="element-314">
                  Close
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}